import { Component } from '@theme/component';

/**
 * OC · Section Heading.
 *
 * Reveals the decorative underline the first time the heading enters the viewport, by adding
 * `oc-sh--revealed` (the CSS drives the `scaleX(0) -> scaleX(1)` transition). Falls back to the
 * revealed state immediately when the animation is off, when the user prefers reduced motion, or
 * when `IntersectionObserver` is unavailable.
 *
 * @extends Component
 */
class OcSectionHeadingComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.classList.contains('oc-sh--revealed')) return;

    const reduceMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.classList.contains('oc-sh--animate') || reduceMotion || !('IntersectionObserver' in window)) {
      this.classList.add('oc-sh--revealed');
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.classList.add('oc-sh--revealed');
          this.#stopObserving();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stopObserving();
    super.disconnectedCallback();
  }

  #stopObserving() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('oc-section-heading-component')) {
  customElements.define('oc-section-heading-component', OcSectionHeadingComponent);
}
